import Button from "@/common/components/Button";
import CardNumberWithCopyButton from "@/common/components/CardNumberWithCopyButton";
import { BillingEventData } from "@/common/types/billing/event.type";
import { ClientFleetCardData } from "@/common/types/client/card.type";
import { formatCurrency, formatDateTime, formatDecimal, formatUnit } from "@/common/utils/formatters";
import { transformToNumber } from "@/common/utils/transformers";
import { DownloadIcon, FileClockIcon, SearchIcon } from "lucide-react";
import React from "react";
import { Tooltip } from "react-tooltip";
import CompanyBillDetails from "../components/companyBillDetails";
import CompanyConsumptionDetails from "../components/companyConsumptionDetails";
import CompanyEventTotalFee from "../components/companyConsumptionTotalFee";
import CompanyRefundDetails from "../components/companyRefundDetails";
import PrintReceiptButton from "@/common/components/PrintReceiptButton";
import { eventableStatus } from "../constants/events/eventableStatus.constant";
import { cn } from "@/common/utils/cn";

export class CompanyBillingEventTransformer {
  static tableTransformer({
    type,
    fetchBillingEvent,
    redirectToConsumption,
    redirectToConsumptionLogs,
    disableActions,
  }: {
    type?: string
    fetchBillingEvent?: (id: string) => void
    redirectToConsumption?: (props: {
      cardId: string
      establishment: { id: string; label: string }
      product: { id: string; displayName: string }
    }) => void
    redirectToConsumptionLogs?: ({eventId}: {eventId: string}) => void
    disableActions?: boolean
  }) {
    return (events: BillingEventData[]) => {
      return events.map((event) => {
        const originalEventable = event.eventable
        const eventable = originalEventable?.refundable
          ?? originalEventable?.related_eventable?.refundable
          ?? originalEventable?.related_eventable
          ?? originalEventable;

        const eventStatus = event?.status as keyof typeof eventableStatus;

        return ({
          _className: cn(
            event.eventable_type === 'Billing::Refund' && 'bg-danger-op',
            eventStatus === 'pending' && 'bg-yellow-op-2',
          ),
          status: eventableStatus[eventStatus] || eventStatus,
          protocol_number: event.protocol_number,
          bill: '',
          eventable_type: eventableTypeTranslations[event.eventable_type] || 'Nao implementado',
          details: eventableDetailsComponent[event.eventable_type]
            ? React.createElement(eventableDetailsComponent[event.eventable_type], { data: event.eventable, type })
            : '',
          from_client_balance: (
            <div className="flex items-start">
              <span className="bg-neutral-op-8 p-2 rounded">
                {event.eventable?.from_client_balance ? 'Pré-pago' : 'Pós-pago'}
              </span>
            </div>
          ),
          license_plate: (eventable?.card as ClientFleetCardData)?.equipment_field_for_display_value || '',
          total_amount: event.total_amount
            ? formatCurrency(
              transformToNumber({
                value: event.total_amount,
                invert: true,
              }) + transformToNumber({ value: event.fee_total })
            )
            : '',
          client_fee_total: event.fee_total
            ? <CompanyEventTotalFee data={event} />
            : '',
          establishment_fee_total: event.related_events_fee_total
            ? <CompanyEventTotalFee data={event} kind="establishment" />
            : '',
          income_tax_total: event.income_tax_total ? formatCurrency(parseFloat(event.income_tax_total)) : '',
          branch: event?.branch_name || '',
          card: eventable?.card ? (
            <CardNumberWithCopyButton card={eventable?.card} />
          ) : '',
          usage_declaration: eventable?.current_equipment_usage
            ? formatUnit(formatDecimal(eventable?.current_equipment_usage, 0), eventable?.current_equipment_usage_unit)
            : '',
          author: event?.author?.full_name || '',
          capture_method: eventable?.capture_method?.display_name || originalEventable?.capture_method?.display_name || '',
          created_at: formatDateTime(new Date(event?.created_at || 0)),
          actions: (!!fetchBillingEvent || !!redirectToConsumption) && (
            <div className="flex items-center gap-2">
              {['Client::Consumption', 'Billing::Refund'].includes(event.eventable_type) && eventStatus === 'confirmed'
                ? <PrintReceiptButton eventId={event.protocol_number} showConfirmationModal />
                : null}

              {event.eventable_type === 'Billing::Bill' && !!fetchBillingEvent && (
                <Button
                  className="bg-transparent hover:bg-transparent disabled:hover:bg-transparent p-0 mx-auto"
                  onClick={() => fetchBillingEvent(event.id)}
                  disabled={disableActions}
                  operationKlassToCheckPermission="Admin::Billing::Events::Show"
                >
                  <DownloadIcon strokeWidth={1} />
                </Button>
              )}

              {event.eventable_type === 'Client::Consumption' && !!redirectToConsumptionLogs && (
                <Button
                  name="access_consumption_logs"
                  variant='link-white'
                  className="w-full"
                  operationKlassToCheckPermission="Admin::Anp::HistoricalReports::Show"
                  onClick={() => redirectToConsumptionLogs({eventId: event?.eventable?.id || ''})}
                >
                  <FileClockIcon strokeWidth={1} />
                </Button>
              )}

              {event.eventable_type === 'Client::Consumption' && !!redirectToConsumption && (
                <>
                  <Button
                    data-tooltip-id={`${event.protocol_number}`}
                    className="bg-transparent hover:bg-transparent disabled:hover:bg-transparent p-0 mx-auto"
                    onClick={() =>
                      redirectToConsumption({
                        cardId: event.eventable?.card?.id || '',
                        establishment: {
                          id: event.eventable?.establishment_branch?.id || '',
                          label:
                            `${event.eventable?.establishment_branch?.general_information?.fantasy_name} (${event.eventable?.establishment_branch?.general_information?.official_name})` ||
                            '',
                        },
                        product: {
                          id: event.eventable?.line_items?.[0]?.product?.id || '',
                          displayName: event.eventable?.line_items?.[0]?.product?.display_name || '',
                        },
                      })
                    }
                    disabled={disableActions}
                    operationKlassToCheckPermission="Admin::Client::Cards::CurrentLimits"
                  >
                    <SearchIcon strokeWidth={1} />
                  </Button>

                  <Tooltip id={`${event.protocol_number}`}>
                    Acesso direto à tela de consulta para o equipamento e estabelecimento dessa transação.
                  </Tooltip>
                </>
              )}
            </div>
          ),
        });
      });
    };
  }

  static quickSearchModelTransformer() {
    return (events: BillingEventData[]) => {
      return events.map((event) => ({
        route: `companies/${event.company_id}/transactions?fromInternalBase%5Blabel%5D=Todas&fromInternalBase%5Bvalue%5D=&search=${event.protocol_number}`,
        items: [
          ['Transação', event.protocol_number],
          ['Tipo', eventableTypeTranslations[event.eventable_type]],
          ['Empresa', event.eventable?.client_branch?.general_information?.fantasy_name],
          ['Detalhes', eventableDetailsQuickSearchValue[event.eventable_type] ? eventableDetailsQuickSearchValue[event.eventable_type](event) : ''],
        ],
      }));
    };
  }
}

export const eventableDetailsComponent: { [key: string]: React.ComponentType<any>; } = {
  'Client::Consumption': CompanyConsumptionDetails,
  'Billing::Refund': CompanyRefundDetails,
  'Billing::Bill': CompanyBillDetails,
  'Billing::TefTransaction': CompanyConsumptionDetails,
};

export const eventableDetailsQuickSearchValue: { [key: string]: (event: BillingEventData) => string; } = {
  'Client::Consumption': (event) => `Estabelecimento: ${event?.eventable?.establishment_branch?.general_information?.fantasy_name}`,
  'Billing::Refund': (event) => `N. Protocolo: ${event?.eventable?.refundable?.protocol_number}`,
  'Billing::Bill': () => 'Tarifa de Fatura',
  'Billing::TefTransaction': (event) => `Estabelecimento: ${event?.eventable?.establishment_branch?.general_information?.fantasy_name}`,
};

export const eventableTypeTranslations: { [key: string]: string; } = {
  'Client::Consumption': 'Consumo',
  'Billing::Refund': 'Estorno',
  'Billing::Bill': 'Fatura',
  'Billing::TefTransaction': 'Tarifa TEF',
};
